import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { NarrativeCorrelation } from './types';

// Same fetch/unread-count/mark-read/poll/click-outside pattern as
// components/GEOINTNotifications.tsx, re-skinned to this module's
// dark-terminal palette instead of the shadcn light tokens, and reading
// /social/correlations (cross-stream narrative-overlap alerts) instead of
// GEOINT change-detection notifications.

interface AlertsBellProps {
  onViewAll?: () => void;
}

export const AlertsBell: React.FC<AlertsBellProps> = ({ onViewAll }) => {
  const [alerts, setAlerts] = useState<NarrativeCorrelation[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const res: any = await apiClient.getMyCorrelations(undefined, 20);
      setAlerts(res?.correlations ?? []);
      setUnreadCount(res?.unread_count ?? 0);
    } catch {
      // best-effort — bell just stays at its last known state
    } finally {
      setLoading(false);
    }
  };

  const acknowledge = async (a: NarrativeCorrelation) => {
    if (a.status === 'acknowledged' || !a.my_stream_id) return;
    try {
      await apiClient.acknowledgeStreamCorrelation(a.my_stream_id, a.id);
      setAlerts(prev => prev.map(x => x.id === a.id ? { ...x, status: 'acknowledged' } : x));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch { /* best-effort */ }
  };

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const formatTime = (dateStr: string) => {
    const diffMin = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return `${Math.floor(diffHr / 24)}d ago`;
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => { setIsOpen(v => !v); if (!isOpen) fetchAlerts(); }}
        className="relative p-1.5 rounded-md transition-colors"
        style={{ background: isOpen ? '#00ff0010' : 'transparent' }}
        title="Narrative overlap alerts"
      >
        <Bell size={16} style={{ color: unreadCount > 0 ? '#eab308' : '#6b7280' }} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 rounded-lg z-50 max-h-[480px] flex flex-col"
          style={{ background: '#0f0f0f', border: '1px solid #2a2a2a', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}>
          <div className="flex items-center justify-between p-3" style={{ borderBottom: '1px solid #1a1a1a' }}>
            <span className="text-xs font-mono font-bold uppercase tracking-widest" style={{ color: '#eab308' }}>Narrative Overlaps</span>
            {onViewAll && (
              <button onClick={() => { onViewAll(); setIsOpen(false); }} className="text-[11px] text-gray-500 hover:text-gray-300">
                View all
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && alerts.length === 0 && (
              <div className="p-6 text-center text-xs text-gray-600">Loading...</div>
            )}
            {!loading && alerts.length === 0 && (
              <div className="p-6 text-center">
                <p className="text-xs text-gray-600">No overlaps detected yet.</p>
                <p className="text-[10px] text-gray-700 mt-1">You'll see an alert here when the same story surfaces across two of your monitored keywords.</p>
              </div>
            )}
            {alerts.map(a => (
              <div key={a.id}
                className="p-3 cursor-pointer transition-colors"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: a.status === 'new' ? '#eab30808' : 'transparent' }}
                onClick={() => acknowledge(a)}
              >
                <div className="flex items-start gap-2">
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded mt-0.5 font-mono flex-shrink-0"
                    style={{ background: '#eab30820', color: '#eab308' }}>
                    {Math.round(a.overlap_score * 100)}%
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-white truncate">
                        "{a.my_keyword}" ↔ "{a.other_keyword}"
                      </span>
                      <span className="text-[10px] text-gray-600 flex-shrink-0">{formatTime(a.detected_at)}</span>
                    </div>
                    {a.shared_hashtags.length > 0 && (
                      <p className="text-[10px] text-gray-500 mt-1 truncate">
                        Shared: {a.shared_hashtags.slice(0, 4).map(t => `#${t}`).join(', ')}
                      </p>
                    )}
                  </div>
                  {a.status === 'new' && <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: '#eab308' }} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertsBell;
